import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { consumeCallback } from '../lib/oauth'
import { api, ApiClientError } from '../lib/api'
import { saveSession } from '../lib/storage'

// Module-level guard: React 18 StrictMode mounts effects twice in dev.
// `consumeCallback` mutates sessionStorage (clears state/verifier) and
// Google rejects reusing an OAuth code — so we must run the exchange
// exactly once per callback URL. Keying by `code` lets a user retry
// (which produces a fresh code) without restarting the SPA.
// (Vercel rule `advanced-init-once`.)
let processedCode: string | null = null

export function AuthCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('正在完成登录…')

  useEffect(() => {
    const code = params.get('code')
    if (!code) return
    if (processedCode === code) return
    processedCode = code
    void (async () => {
      try {
        const { code, codeVerifier } = consumeCallback(params)
        setStatus('正在与 OnlyBans 服务端确认身份…')
        const resp = await api.exchangeGoogle({
          code,
          codeVerifier,
          redirectUri: `${window.location.origin}/auth/callback`,
        })
        saveSession({ token: resp.session.token, expiresAt: resp.session.expiresAt })

        // Notify the OnlyBans browser extension (if installed) so it can
        // pick up the session token from this page.
        document.dispatchEvent(
          new CustomEvent('OnlyBansLoginSuccess', {
            detail: {
              sessionToken: resp.session.token,
              expiresAt: resp.session.expiresAt,
              user: resp.user,
              subscription: resp.subscription,
            },
          }),
        )

        navigate('/dashboard', { replace: true })
      } catch (e) {
        if (e instanceof ApiClientError) {
          setError(`${e.error.code}: ${e.error.message}`)
        } else {
          setError(e instanceof Error ? e.message : 'Login failed')
        }
      }
    })()
    // No cleanup needed: setState on unmounted component is a no-op in
    // React 18+ (no warning), and we *want* the eventual error/success
    // setState to land if the user is still on this page.
  }, [params, navigate])

  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center">
      {error ? (
        <>
          <h1 className="text-2xl font-semibold text-red-400">登录失败</h1>
          <p className="mt-3 text-sm text-zinc-400">{error}</p>
          <a
            href="/login"
            className="mt-6 inline-block text-sm underline text-zinc-300 hover:text-zinc-100"
          >
            重试
          </a>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold">{status}</h1>
          <p className="mt-3 text-sm text-zinc-400">请稍候，不要关闭窗口。</p>
        </>
      )}
    </div>
  )
}
